import { registerAuditLabels, type StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo authority (ver modules/authority/README.md). */

export type Platform =
  | "linkedin" | "lattes" | "orcid" | "researchgate" | "scholar" | "instagram" | "site" | "youtube" | "x" | "tiktok" | "whatsapp" | "newsletter" | "podcast" | "outro";

export const PLATFORM_LABEL: Record<Platform, string> = {
  linkedin: "LinkedIn",
  lattes: "Lattes",
  orcid: "ORCID",
  researchgate: "ResearchGate",
  scholar: "Google Acadêmico",
  instagram: "Instagram",
  site: "Site",
  youtube: "YouTube",
  x: "X",
  tiktok: "TikTok",
  whatsapp: "WhatsApp",
  newsletter: "Newsletter",
  podcast: "Podcast",
  outro: "Outro",
};

export const PROFILE_STATUS: StatusMap = {
  not_created: { label: "não criado", tone: "neutral" },
  outdated: { label: "desatualizado", tone: "warn" },
  in_repair: { label: "em reparo", tone: "info" },
  ok: { label: "em dia", tone: "ok" },
  reserved: { label: "nome reservado", tone: "neutral" },
  not_priority: { label: "fora da prioridade", tone: "neutral" },
};

export const CHECK_STATUS: StatusMap = {
  todo: { label: "a fazer", tone: "neutral" },
  done: { label: "feito", tone: "ok" },
  skipped: { label: "dispensado", tone: "neutral" },
};

export const STAGE: StatusMap = {
  identified: { label: "identificado", tone: "neutral" },
  connected: { label: "conectado", tone: "info" },
  engaged: { label: "em conversa pública", tone: "info" },
  conversation: { label: "conversa de 20 min", tone: "accent" },
  proposal: { label: "proposta", tone: "accent" },
  client: { label: "cliente", tone: "ok" },
  paused: { label: "pausado", tone: "warn" },
};

export const SECTOR_LABEL: Record<string, string> = { saude: "Saúde", educacao: "Educação", publico: "Setor público", empresa: "Empresa", outro: "Outro" };

export const TOUCH_LABEL: Record<string, string> = {
  connection: "Conexão",
  comment: "Comentário substantivo",
  message: "Mensagem",
  coffee: "Café",
  call: "Ligação",
  meeting: "Reunião",
  other: "Outro",
};

export const APPEARANCE_KIND: Record<string, string> = { interview: "Entrevista", podcast: "Podcast", talk: "Palestra", panel: "Painel", guest_article: "Artigo convidado" };
export const APPEARANCE_STATUS: StatusMap = {
  prospect: { label: "a abordar", tone: "neutral" },
  pitched: { label: "proposta enviada", tone: "info" },
  scheduled: { label: "agendada", tone: "accent" },
  done: { label: "feita", tone: "ok" },
  declined: { label: "recusada", tone: "warn" },
};
export const PUBLICATION_KIND: Record<string, string> = { article: "Artigo", book: "Livro", chapter: "Capítulo", report: "Relatório", preprint: "Preprint", thesis: "Tese", long_post: "Texto longo" };
export const PUBLICATION_STATUS: StatusMap = {
  idea: { label: "ideia", tone: "neutral" },
  draft: { label: "rascunho", tone: "neutral" },
  submitted: { label: "submetida", tone: "info" },
  in_press: { label: "no prelo", tone: "accent" },
  published: { label: "publicada", tone: "ok" },
};
export const SEARCH_STATUS: StatusMap = { pending: { label: "pendente", tone: "warn" }, done: { label: "feita", tone: "ok" }, dropped: { label: "descartada", tone: "neutral" } };
export const DOSSIER_STATUS: StatusMap = { open: { label: "aberta", tone: "warn" }, in_progress: { label: "em andamento", tone: "info" }, done: { label: "resolvida", tone: "ok" } };
export const BOOK_STATUS: StatusMap = {
  idea: { label: "ideia", tone: "neutral" },
  outline: { label: "sumário", tone: "info" },
  writing: { label: "escrevendo", tone: "info" },
  review: { label: "revisão", tone: "accent" },
  published: { label: "publicado", tone: "ok" },
};
export const PERIOD_LABEL: Record<string, string> = { week: "na semana", month: "no mês", quarter: "no trimestre", year: "no ano", once: "no total" };

export interface ChecklistItem { id: string; area: Platform; session: number | null; position: number; title: string; detail: string | null; status: "todo" | "done" | "skipped"; due_on: string | null; evidence_url: string | null; source_ref: string | null; done_at: string | null }
export interface Profile { id: string; platform: Platform; handle: string | null; url: string | null; role_text: string | null; decision: string | null; cadence: string | null; status: string; priority: number | null; last_reviewed_at: string | null; notes: string | null }
export interface DossierEntry { id: string; code: string; source: string; kind: string | null; says: string | null; relevance: string | null; gap_risk: string | null; action: string | null; status: string; verified: boolean; url: string | null }
export interface SearchRow { id: string; query: string; engine: string | null; purpose: string | null; related_code: string | null; status: string; due_on: string | null; result_summary: string | null; result_url: string | null; done_at: string | null }
export interface PublicationRow { id: string; kind: string; title: string; venue: string | null; year: number | null; doi: string | null; url: string | null; coauthors: string | null; relevance: string | null; status: string; featured: boolean; show_on_site?: boolean }
export interface Appearance { id: string; kind: string; outlet: string; host: string | null; topic: string | null; audience: string | null; happens_on: string | null; status: string; recorded: boolean; url: string | null; notes: string | null }
export interface BookView { id: string; title: string; subtitle: string | null; status: string; target_date: string | null; notes: string | null; chapters: number; chapters_done: number; words_done: number; words_target: number }
export interface Chapter { id: string; book_id: string; position: number; title: string; summary: string | null; status: string; words_target: number | null; words_done: number }
export interface DecisionMaker {
  id: string;
  person_id: string | null;
  name: string;
  role_title: string | null;
  institution: string | null;
  sector: string;
  region: string | null;
  linkedin_url: string | null;
  stage: string;
  priority: number;
  next_action: string | null;
  next_action_on: string | null;
  last_touch_at: string | null;
  active: boolean;
  notes: string | null;
  touches_total: number;
  touches_7d: number;
  last_touch_kind: string | null;
}
export interface Goal { id: string; key: string; title: string; target: number; unit: string | null; period: string; source: string; due_on: string | null; active: boolean; source_ref: string | null; progress: number }
export interface Overview { org_id: string; checklist_total: number; checklist_done: number; linkedin_total: number; linkedin_done: number; decision_makers_active: number; comments_this_week: number; searches_pending: number; dossier_open: number; appearances_open: number }

declare module "@vixio/data" {
  interface RpcRegistry {
    "authority.upsert_checklist_item": { args: { p_org: string; p_area: Platform; p_title: string; p_session?: number | null; p_position?: number; p_detail?: string | null; p_due_on?: string | null; p_source_ref?: string | null; p_id?: string | null }; returns: string };
    "authority.set_checklist_status": { args: { p_item: string; p_status: "todo" | "done" | "skipped"; p_evidence_url?: string | null }; returns: null };
    "authority.upsert_profile": { args: { p_org: string; p_platform: Platform; p_status: string; p_url?: string | null; p_handle?: string | null; p_role_text?: string | null; p_decision?: string | null; p_cadence?: string | null; p_priority?: number | null; p_notes?: string | null }; returns: string };
    "authority.mark_profile_reviewed": { args: { p_profile: string }; returns: null };
    "authority.upsert_dossier_entry": { args: { p_org: string; p_code: string; p_source: string; p_kind?: string | null; p_says?: string | null; p_relevance?: string | null; p_gap_risk?: string | null; p_action?: string | null; p_status?: string; p_verified?: boolean; p_url?: string | null }; returns: string };
    "authority.upsert_search": { args: { p_org: string; p_query: string; p_engine?: string | null; p_purpose?: string | null; p_related_code?: string | null; p_due_on?: string | null; p_id?: string | null }; returns: string };
    "authority.complete_search": { args: { p_search: string; p_result_summary: string; p_result_url?: string | null; p_status?: string }; returns: null };
    "authority.upsert_publication": { args: { p_org: string; p_kind: string; p_title: string; p_year?: number | null; p_venue?: string | null; p_doi?: string | null; p_url?: string | null; p_coauthors?: string | null; p_relevance?: string | null; p_status?: string; p_featured?: boolean; p_id?: string | null }; returns: string };
    "authority.upsert_appearance": { args: { p_org: string; p_kind: string; p_outlet: string; p_status?: string; p_topic?: string | null; p_host?: string | null; p_happens_on?: string | null; p_url?: string | null; p_audience?: string | null; p_recorded?: boolean; p_notes?: string | null; p_id?: string | null }; returns: string };
    "authority.upsert_book": { args: { p_org: string; p_title: string; p_status?: string; p_subtitle?: string | null; p_target_date?: string | null; p_notes?: string | null; p_id?: string | null }; returns: string };
    "authority.upsert_chapter": { args: { p_book: string; p_position: number; p_title: string; p_status?: string; p_summary?: string | null; p_words_target?: number | null; p_words_done?: number }; returns: string };
    "authority.upsert_decision_maker": {
      args: {
        p_org: string;
        p_name: string;
        p_sector?: string;
        p_role_title?: string | null;
        p_institution?: string | null;
        p_region?: string | null;
        p_linkedin_url?: string | null;
        p_person?: string | null;
        p_stage?: string;
        p_priority?: number;
        p_next_action?: string | null;
        p_next_action_on?: string | null;
        p_notes?: string | null;
        p_active?: boolean;
        p_id?: string | null;
      };
      returns: string;
    };
    "authority.log_touch": { args: { p_decision_maker: string; p_kind: string; p_note?: string | null; p_happened_at?: string | null }; returns: string };
    "authority.upsert_goal": { args: { p_org: string; p_key: string; p_title: string; p_target: number; p_period: string; p_source?: string; p_unit?: string | null; p_due_on?: string | null; p_active?: boolean; p_source_ref?: string | null }; returns: string };
    "authority.set_publication_public": { args: { p_publication: string; p_public: boolean }; returns: null };
    "authority.record_goal_progress": { args: { p_goal: string; p_amount: number; p_note?: string | null; p_happened_at?: string | null }; returns: string };
  }
}

registerAuditLabels({
  "authority_checklist.upserted": "Item do checklist salvo",
  "authority_checklist.status_set": "Item do checklist atualizado",
  "authority_profile.upserted": "Plataforma atualizada",
  "authority_profile.reviewed": "Perfil revisado",
  "authority_dossier.upserted": "Dossiê atualizado",
  "authority_search.upserted": "Busca registrada",
  "authority_search.completed": "Busca concluída",
  "authority_publication.upserted": "Publicação salva",
  "authority_publication.site": "Publicação no site alterada",
  "authority_appearance.upserted": "Entrevista ou podcast salvo",
  "authority_book.upserted": "Livro atualizado",
  "authority_chapter.upserted": "Capítulo atualizado",
  "authority_decision_maker.upserted": "Decisor salvo",
  "authority_touch.logged": "Toque registrado",
  "authority_goal.upserted": "Meta salva",
  "authority_goal.progress": "Progresso de meta lançado",
});
